import React, { useState, useEffect, useMemo } from 'react';
import ConfirmDialog from '../../../shared/components/ui/ConfirmDialog';
import { validateWalkinPhone } from '../utils/walkinPhone';
import './WalkinBookingModal.css';

const parseTimeString = (timeStr) => {
  if (!timeStr) return null;
  const [hours] = timeStr.split(':').map(Number);
  return hours;
};

const formatDateInput = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const resolveBookingServiceUuids = (booking) => {
  if (!booking) return [];
  if (Array.isArray(booking.services) && booking.services.length) {
    return booking.services
      .map((s) => s?.serviceUuid)
      .filter(Boolean);
  }
  if (booking.serviceUuid) return [booking.serviceUuid];
  return [];
};

const EditWalkinBookingModal = ({
  booking,
  open,
  onClose,
  services,
  settings,
  onSubmit,
  isLoading,
}) => {
  const [selectedServiceUuids, setSelectedServiceUuids] = useState([]);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [selectedHour, setSelectedHour] = useState(9);
  const [selectedMinute, setSelectedMinute] = useState(0);
  const [selectedDateKey, setSelectedDateKey] = useState('');
  const [overbookPayload, setOverbookPayload] = useState(null);
  const [isOverbookSubmitting, setIsOverbookSubmitting] = useState(false);

  const todayKey = useMemo(() => formatDateInput(new Date()), []);

  // Available hours derived from store hours for the *currently selected* date
  const availableHours = useMemo(() => {
    if (!settings || !settings.weekdayOpen || !settings.weekdayClose) {
      return Array.from({ length: 13 }, (_, i) => i + 9);
    }
    const dateForLookup = selectedDateKey
      ? new Date(`${selectedDateKey}T00:00:00`)
      : new Date();
    const dayOfWeek = dateForLookup.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    const openTime = isWeekend ? settings.weekendOpen : settings.weekdayOpen;
    const closeTime = isWeekend ? settings.weekendClose : settings.weekdayClose;

    const startHour = parseTimeString(openTime);
    const endHour = parseTimeString(closeTime);
    if (startHour === null || endHour === null) {
      return Array.from({ length: 13 }, (_, i) => i + 9);
    }
    const hours = [];
    for (let h = startHour; h <= endHour; h += 1) hours.push(h);
    return hours.length > 0 ? hours : Array.from({ length: 13 }, (_, i) => i + 9);
  }, [settings, selectedDateKey]);

  // Multi-service cap
  const multiServiceEnabled = Boolean(settings?.multiServiceBookingsEnabled);
  const maxAllowed = multiServiceEnabled
    ? Math.max(1, Number(settings?.maxServicesAllowed) || 3)
    : 1;

  // Pre-populate when modal opens
  useEffect(() => {
    if (open && booking) {
      setSelectedServiceUuids(resolveBookingServiceUuids(booking));
      setCustomerName(booking.customerName || '');
      setCustomerPhone(booking.customerPhone || '');
      setNotes(booking.notes || '');
      setError('');

      if (booking.appointmentStartTime) {
        const startTime = new Date(booking.appointmentStartTime);
        setSelectedHour(startTime.getHours());
        setSelectedMinute(startTime.getMinutes());
        setSelectedDateKey(formatDateInput(startTime));
      } else {
        setSelectedHour(9);
        setSelectedMinute(0);
        setSelectedDateKey(formatDateInput(new Date()));
      }
    }
  }, [open, booking]);

  const appointmentStartTime = useMemo(() => {
    if (!selectedDateKey) return null;
    const base = new Date(`${selectedDateKey}T00:00:00`);
    base.setHours(selectedHour);
    base.setMinutes(selectedMinute);
    base.setSeconds(0);
    base.setMilliseconds(0);
    return base;
  }, [selectedDateKey, selectedHour, selectedMinute]);

  const totalDurationMins = useMemo(() => {
    if (!services || selectedServiceUuids.length === 0) return 0;
    const byUuid = new Map(services.map((s) => [s.serviceUuid, s]));
    return selectedServiceUuids.reduce((sum, uuid) => {
      const svc = byUuid.get(uuid);
      return sum + (Number(svc?.appointmentDurationMins) || 30);
    }, 0);
  }, [services, selectedServiceUuids]);

  const estimatedEndTime = useMemo(() => {
    if (totalDurationMins === 0 || !appointmentStartTime) return null;
    const endTime = new Date(appointmentStartTime.getTime() + totalDurationMins * 60000);
    return endTime.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }, [totalDurationMins, appointmentStartTime]);

  const originalServiceUuids = useMemo(() => resolveBookingServiceUuids(booking), [booking]);

  const handleServiceClick = (serviceUuid) => {
    setError('');
    setSelectedServiceUuids((prev) => {
      if (prev.includes(serviceUuid)) {
        // Always keep at least one selected — clearing the set is meaningless.
        if (prev.length === 1) return prev;
        return prev.filter((uuid) => uuid !== serviceUuid);
      }
      if (!multiServiceEnabled) {
        return [serviceUuid];
      }
      if (prev.length >= maxAllowed) {
        setError(`Maximum ${maxAllowed} services per booking`);
        return prev;
      }
      return [...prev, serviceUuid];
    });
  };

  const handleSubmit = async () => {
    if (selectedServiceUuids.length === 0) {
      setError('Please select at least one service');
      return;
    }
    if (!customerName.trim()) {
      setError('Please enter a customer name');
      return;
    }
    // CP-111 (W1): phone is mandatory on the walk-in card, edits included —
    // the identity link (and loyalty stamping) follows it.
    const phoneError = validateWalkinPhone(customerPhone);
    if (phoneError) {
      setError(phoneError);
      return;
    }
    if (!appointmentStartTime) {
      setError('Pick a valid date');
      return;
    }
    const startDay = new Date(appointmentStartTime);
    startDay.setHours(0, 0, 0, 0);
    const todayDay = new Date();
    todayDay.setHours(0, 0, 0, 0);
    if (startDay < todayDay) {
      setError('Cannot book appointments for past dates');
      return;
    }

    setError('');

    const year = appointmentStartTime.getFullYear();
    const month = String(appointmentStartTime.getMonth() + 1).padStart(2, '0');
    const day = String(appointmentStartTime.getDate()).padStart(2, '0');
    const hours = String(appointmentStartTime.getHours()).padStart(2, '0');
    const minutes = String(appointmentStartTime.getMinutes()).padStart(2, '0');
    const seconds = String(appointmentStartTime.getSeconds()).padStart(2, '0');
    const appointmentStartTimeISO = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;

    const previousKey = [...originalServiceUuids].sort().join(',');
    const nextKey = [...selectedServiceUuids].sort().join(',');

    const payload = {};
    if (nextKey !== previousKey) {
      // Send `serviceUuids` for any change. Backend now supports multi-
      // service edits — it flows through the same aggregator the create
      // path uses, rewrites the child rows in one tx, and recomputes the
      // end time from summed durations.
      payload.serviceUuids = selectedServiceUuids;
    }
    if (customerName.trim() !== booking.customerName) {
      payload.customerName = customerName.trim();
    }
    if (customerPhone.trim() !== (booking.customerPhone || '')) {
      payload.customerPhone = customerPhone.trim();
    }
    if (appointmentStartTimeISO !== booking.appointmentStartTime) {
      payload.appointmentStartTime = appointmentStartTimeISO;
    }
    if (notes.trim() !== (booking.notes || '')) {
      payload.notes = notes.trim() || null;
    }

    if (Object.keys(payload).length === 0) {
      setError('No changes detected');
      return;
    }

    try {
      await onSubmit(payload);
    } catch (err) {
      const apiMessage = err?.data?.message || err?.message || '';
      const status = err?.status || err?.response?.status;
      const isSlotFull = status === 409 && apiMessage.includes('SLOT_FULL');
      if (isSlotFull) {
        // Walk-ins are merchant-driven — stash the payload and surface a
        // styled ConfirmDialog for explicit over-book acknowledgement.
        setOverbookPayload(payload);
        return;
      }
      setError(apiMessage || 'Failed to update walk-in booking');
    }
  };

  const handleOverbookConfirm = async () => {
    if (!overbookPayload) return;
    setIsOverbookSubmitting(true);
    try {
      await onSubmit({ ...overbookPayload, forceOverride: true });
      setOverbookPayload(null);
    } catch (forceErr) {
      setError(forceErr?.data?.message || forceErr?.message || 'Failed to update walk-in booking');
      setOverbookPayload(null);
    } finally {
      setIsOverbookSubmitting(false);
    }
  };

  const handleOverbookCancel = () => {
    setOverbookPayload(null);
    setError('Update cancelled — slot is at capacity.');
  };

  if (!open) return null;

  const activeServices = services?.filter((s) => s.active !== false) || [];

  return (
    <div className="walkin-modal-overlay" onClick={onClose}>
      <div className="walkin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="walkin-modal-header">
          <h3>Edit Walk-In Booking</h3>
          <button className="walkin-modal-close" onClick={onClose} disabled={isLoading}>
            ×
          </button>
        </div>

        <div className="walkin-modal-body">
          <div className="walkin-modal-section">
            <label className="walkin-modal-label">
              Select Service{multiServiceEnabled ? 's' : ''} <span className="required">*</span>
              {multiServiceEnabled && (
                <span className="optional"> (up to {maxAllowed})</span>
              )}
            </label>
            <div className="walkin-modal-services">
              {activeServices.length === 0 ? (
                <div className="walkin-modal-no-services">
                  No active services available
                </div>
              ) : (
                activeServices.map((service) => {
                  const isSelected = selectedServiceUuids.includes(service.serviceUuid);
                  return (
                    <button
                      key={service.serviceUuid}
                      className={`walkin-modal-service-tile ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleServiceClick(service.serviceUuid)}
                      disabled={isLoading}
                      type="button"
                    >
                      <div className="walkin-modal-service-name">{service.serviceName}</div>
                      <div className="walkin-modal-service-meta">
                        {service.appointmentDurationMins || 30} mins
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="walkin-modal-section">
            <label htmlFor="customer-name" className="walkin-modal-label">
              Customer Name <span className="required">*</span>
            </label>
            <input
              id="customer-name"
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Enter customer name"
              disabled={isLoading}
              className="walkin-modal-input"
              maxLength={100}
            />
          </div>

          <div className="walkin-modal-section">
            <label htmlFor="customer-phone" className="walkin-modal-label">
              Customer Phone <span className="required">*</span>
            </label>
            <input
              id="customer-phone"
              type="tel"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              placeholder="Enter phone number"
              disabled={isLoading}
              className="walkin-modal-input"
              maxLength={15}
            />
          </div>

          <div className="walkin-modal-section">
            <label htmlFor="booking-date" className="walkin-modal-label">Booking Date</label>
            <input
              id="booking-date"
              type="date"
              value={selectedDateKey}
              min={todayKey}
              onChange={(e) => setSelectedDateKey(e.target.value)}
              disabled={isLoading}
              className="walkin-modal-input"
            />
          </div>

          <div className="walkin-modal-section">
            <label className="walkin-modal-label">Appointment Time</label>
            <div className="walkin-modal-time-picker">
              <select
                value={selectedHour}
                onChange={(e) => setSelectedHour(Number(e.target.value))}
                disabled={isLoading}
                className="walkin-modal-time-select"
              >
                {availableHours.map((hour) => (
                  <option key={hour} value={hour}>
                    {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
                  </option>
                ))}
              </select>
              <span className="walkin-modal-time-separator">:</span>
              <select
                value={selectedMinute}
                onChange={(e) => setSelectedMinute(Number(e.target.value))}
                disabled={isLoading}
                className="walkin-modal-time-select"
              >
                <option value={0}>00</option>
                <option value={15}>15</option>
                <option value={30}>30</option>
                <option value={45}>45</option>
              </select>
            </div>
            {estimatedEndTime && (
              <div className="walkin-modal-time-hint">
                Estimated end: {estimatedEndTime}
              </div>
            )}
          </div>

          <div className="walkin-modal-section">
            <label htmlFor="notes" className="walkin-modal-label">
              Notes <span className="optional">(Optional)</span>
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any special instructions or notes"
              disabled={isLoading}
              className="walkin-modal-textarea"
              rows={3}
              maxLength={500}
            />
          </div>

          {error && (
            <div className="walkin-modal-error">
              {error}
            </div>
          )}
        </div>

        <div className="walkin-modal-footer">
          <button
            className="walkin-modal-cancel"
            onClick={onClose}
            disabled={isLoading}
            type="button"
          >
            Cancel
          </button>
          <button
            className="walkin-modal-submit"
            onClick={handleSubmit}
            disabled={isLoading}
            type="button"
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={Boolean(overbookPayload)}
        title="Slot already at capacity"
        description="This time slot is already fully booked. As the merchant, you can over-book and proceed with the walk-in. Continue anyway?"
        confirmLabel={isOverbookSubmitting ? 'Saving…' : 'Over-book anyway'}
        cancelLabel="Pick another time"
        confirmVariant="danger"
        busy={isOverbookSubmitting}
        onConfirm={handleOverbookConfirm}
        onCancel={handleOverbookCancel}
      />
    </div>
  );
};

export default EditWalkinBookingModal;
